/**
 * Configuration utility for handling environment variables
 * across development, staging, and production environments.
 */

export type Environment = "development" | "staging" | "production";

interface Config {
  apiUrl: string;
  webUrl: string;
  environment: Environment;
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
}

/**
 * Get the current environment from environment variables
 */
function getEnvironment(): Environment {
  const env =
    import.meta.env.VITE_NODE_ENV || import.meta.env.MODE || "development";

  // Force development mode for localhost URLs to use Vite proxy
  const apiUrl = import.meta.env.VITE_API_URL;

  // If no API URL is set, or it's localhost, stay in development mode
  if (!apiUrl || apiUrl.includes("localhost")) {
    console.log(
      "🔧 No API URL or localhost detected - using development mode with proxy",
    );
    return "development";
  }

  // Only treat as production if it's a remote HTTPS URL
  if (apiUrl?.startsWith("https://") && env === "development") {
    console.log(
      "🔧 HTTPS API URL detected, forcing production mode to bypass proxy",
    );
    return "production";
  }

  if (env === "production") return "production";
  if (env === "staging") return "staging";
  return "development";
}

/**
 * Get the API URL based on the current environment
 */
function getApiUrl(): string {
  const environment = getEnvironment();

  console.log("🔧 Environment:", environment);
  console.log("🔧 API URL env var:", import.meta.env.VITE_API_URL);

  // In development mode, always use the backend URL for direct connection
  // The frontend will use proxy URLs in api.tsx based on isDevelopment flag
  if (environment === "development") {
    return "http://localhost:3000";
  }

  // Use environment variable if provided for non-development
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Fallback to environment-specific defaults
  switch (environment) {
    case "production":
      return "https://api.samphireneuro.com";
    case "staging":
      return "https://preview-api.samphireneuro.com";
    default:
      return "http://localhost:3000";
  }
}

/**
 * Get the web app URL based on the current environment
 */
function getWebUrl(): string {
  const environment = getEnvironment();

  // Use environment variable if provided
  if (import.meta.env.VITE_WEB_URL) {
    return import.meta.env.VITE_WEB_URL;
  }

  console.log("🔧 Environment:", environment);
  console.log("🔧 Web URL:", import.meta.env.VITE_WEB_URL);

  // Fallback to environment-specific defaults
  switch (environment) {
    case "production":
      return "https://web.samphireneuro.com";
    case "staging":
      return "https://preview-web.samphireneuro.com";
    case "development":
    default:
      return "http://localhost:5173";
  }
}

/**
 * Application configuration object
 */
export const config: Config = {
  apiUrl: getApiUrl(),
  webUrl: getWebUrl(),
  environment: getEnvironment(),
  isDevelopment: getEnvironment() === "development",
  isStaging: getEnvironment() === "staging",
  isProduction: getEnvironment() === "production",
};

/**
 * Log configuration on startup (development only)
 */
if (config.isDevelopment) {
  console.log("🔧 App Configuration:", {
    environment: config.environment,
    apiUrl: config.apiUrl,
    webUrl: config.webUrl,
    isDevelopment: config.isDevelopment,
  });
}
